import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { PawPrint, Map, PlusCircle, Heart, Home, ChevronDown, Check, X, ArrowLeft } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { SUBURBS } from "../mock/mock";
import { useAuth } from "../context/AuthContext";
import PawTrail from "./PawTrail";
import SidePaws from "./SidePaws";

const NAV = [
  { to: "/", label: "Rescues", icon: PawPrint },
  { to: "/map", label: "Map", icon: Map },
  { to: "/report", label: "Report", icon: PlusCircle },
  { to: "/lost", label: "Lost", icon: Heart },
  { to: "/carers", label: "Carers", icon: Home },
];

const MENU_ITEMS = [
  { label: "🐾 Rescues", sub: "Browse rescued animals needing a home", to: "/rescues" },
  { label: "🆘 Lost Pets!?", sub: "Help reunite missing pets", to: "/lost" },
  { label: "🔥 Hot Spots (AUS)", sub: "High-activity stray zones · every state", to: "/hotspots" },
  { label: "➕ Report / list an animal", sub: "Found stray · rehome · sell · foster", to: "/report" },
  { label: "⭐ My Favourites", sub: "Rescues you've saved for later", to: "/favourites" },
  { label: "📸 Post Your Pet", sub: "Share photos & videos · likes & comments", to: "/community" },
  { label: "✂️ Dog Groomers", sub: "Grooming salons & mobile groomers near you", to: "/groomers" },
  { label: "🛁 Dog Wash Locations", sub: "Self-serve washes · car washes & servos", to: "/dog-washes" },
  { label: "🏢 List your business", sub: "Add your home-run pet business free", to: "/list-business" },
  { label: "📅 Pawwprints Calendar", sub: "Top 12 pets each year — post yours to feature!", to: "/calendar" },
  { label: "💸 Pet Fundraisers", sub: "Verified GoFundMe links · pet-related only", to: "/fundraisers" },
  { label: "🏆 Pawws Rewards", sub: "Earn points · unlock rewards", to: "/rewards" },
  { label: "🐕 Walk for Rewards", sub: "Walk your dog with our step counter · earn points", to: "/walk" },
  { label: "🤝 Partner with us", sub: "For shelters, rescues & pet businesses", to: "/partners" },
  { label: "📸 AI Breed Scanner", sub: "Snap a stray · identify the breed", to: "/breed-scan" },
  { label: "📖 Breeds A–Z", sub: "Every dog & cat breed indexed", to: "/breeds" },
  { label: "🍲 Special Diets & Recipes", sub: "Senior / health / love", to: "/diets" },
  { label: "🏛️ Council pounds & shelters", sub: "Impounded animals · call within 8 days", to: "/pounds" },
  { label: "📣 Press kit / One-link pitch", sub: "Share Pawwprints with anyone", to: "/press" },
  { label: "💛 Donate to the cause", sub: "Fund vet bills & rescues", to: "/donate/general" },
  { label: "🤝 Volunteer / Foster", sub: "Join the rescue pack", to: "/volunteer" },
  { label: "🕊️ R.I.P. — In Loving Memory", sub: "Memorials & candles", to: "/memorials" },
  { label: "⚠️ Report cruelty (RSPCA)", sub: "Call 1300 852 188 — 24/7", url: "tel:1300852188" },
  { label: "🔎 Microchip lookup", sub: "Find the right registry for your country", to: "/microchip" },
  { label: "🗺️ Live Map", sub: "Strays, lost pets & sightings", to: "/map" },
  { label: "👀 Log a sighting", sub: "Spotted but couldn't catch?", to: "/spot" },
  { label: "🏡 Dog Sitters", sub: "Trusted sitters near you", to: "/sitters" },
  { label: "🐕 Dog Park Locations", sub: "Find off-leash parks near you", to: "/dog-parks" },
  { label: "🏠 Carer homes", sub: "Temporary foster locations", to: "/carers" },
  { label: "🐕 Vets & ER 24/7", sub: "Emergency vet directory", to: "/resources?tab=vets" },
  { label: "🛍️ Pet supplies", sub: "Local pet stores", to: "/resources?tab=stores" },
  { label: "ℹ️ About us", sub: "Our mission + story", to: "/about" },
  { label: "✉️ Contact us / Support", sub: "Questions? We're here to help", url: "mailto:pawwprintsinfo@gmail.com?subject=Pawwprints%20Support" },
];

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [suburb, setSuburb] = useState("Melbourne");
  const [menuOpen, setMenuOpen] = useState(false);

  const points = user?.points_balance ?? 0;
  const prevPoints = useRef(points);
  const [pointsBump, setPointsBump] = useState(false);
  const [pointsDelta, setPointsDelta] = useState(0);
  useEffect(() => {
    if (points > prevPoints.current) {
      setPointsDelta(points - prevPoints.current);
      setPointsBump(true);
      const t = setTimeout(() => setPointsBump(false), 1400);
      prevPoints.current = points;
      return () => clearTimeout(t);
    }
    prevPoints.current = points;
  }, [points]);

  const handleMenuClick = (m) => {
    setMenuOpen(false);
    const scrollToSection = (id) => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else window.scrollTo({ top: 0, behavior: "smooth" });
    };
    if (m.url) {
      window.open(m.url, "_blank");
    } else if (m.to === location.pathname) {
      if (m.scrollTo) scrollToSection(m.scrollTo);
      else window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setTimeout(() => {
        navigate(m.to);
        if (m.scrollTo) setTimeout(() => scrollToSection(m.scrollTo), 450);
      }, 100);
    }
  };

  return (
    <>
    <SidePaws />
    <div className="app-shell text-slate-900">
      <PawTrail />
      {/* Top logo pill */}
      <div className="relative z-10 flex justify-center pt-3 pb-1">
        <Link to="/" data-testid="home-logo-link" className="relative overflow-hidden bg-white rounded-full px-9 py-3 shadow-[0_6px_22px_rgba(224,122,95,0.4)] flex items-center border-[4px] border-[#1B2A4E]">
          <span className="logo-walker logo-walker-top" aria-hidden="true">
            <PawPrint className="logo-walker-paw w-2.5 h-2.5 text-[#1B2A4E]" fill="currentColor" />
            <PawPrint className="logo-walker-paw w-2.5 h-2.5 text-[#1B2A4E]" fill="currentColor" />
          </span>
          <span className="logo-walker logo-walker-bottom" aria-hidden="true">
            <PawPrint className="logo-walker-paw w-2.5 h-2.5 text-[#1B2A4E]" fill="currentColor" />
            <PawPrint className="logo-walker-paw w-2.5 h-2.5 text-[#1B2A4E]" fill="currentColor" />
          </span>
          <span className="paw-logo text-[#1B2A4E] text-[30px] leading-none">Paww</span>
          <span className="flex items-center mx-2">
            <PawPrint className="w-6 h-6 text-[#0B0B0B] -rotate-[20deg] -mr-1.5" fill="currentColor" />
            <PawPrint className="w-6 h-6 text-[#0B0B0B] rotate-[18deg] mt-1" fill="currentColor" />
          </span>
          <span className="paw-logo text-[#1B2A4E] text-[30px] leading-none">prints</span>
          <sup className="text-[13px] text-[#1B2A4E] font-extrabold ml-1">™</sup>
        </Link>
      </div>

      {/* Menu + location row */}
      <div className="relative z-10 px-4 pt-2 pb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {location.pathname !== "/" && (
            <button
              data-testid="global-back-btn"
              onClick={() => navigate(-1)}
              className="bg-white text-[#1B2A4E] rounded-full w-9 h-9 flex items-center justify-center shadow-md active:scale-95 transition-transform"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          <button
            data-testid="menu-open-btn"
            onClick={() => setMenuOpen(true)}
            className="bg-[var(--paw-navy-2)] text-white rounded-full pl-4 pr-4 py-2.5 flex items-center gap-2 text-sm font-extrabold shadow-md active:scale-95 transition-transform"
          >
            <PawPrint className="w-[18px] h-[18px] text-[var(--paw-orange)]" fill="currentColor" />
            Menu
            <ChevronDown className="w-[18px] h-[18px]" />
          </button>
        </div>

        {!user && (
          <div className="flex flex-col items-center leading-none">
            <button
              data-testid="header-signup-btn"
              onClick={() => navigate("/auth?mode=signup")}
              className="signup-link text-[#16264F] font-extrabold text-sm flex items-center gap-1 active:scale-95 transition-transform"
            >
              <PawPrint className="signup-paw w-4 h-4 text-[var(--paw-orange)]" fill="currentColor" />
              Sign&nbsp;Up
            </button>
            <button
              data-testid="header-login-btn"
              onClick={() => navigate("/auth?mode=signin")}
              className="login-link text-[#16264F] font-bold text-xs mt-1.5 flex items-center gap-1 active:scale-95 transition-transform"
            >
              Log&nbsp;in
              <PawPrint className="signup-paw w-3.5 h-3.5 text-[#4F86C6]" fill="currentColor" />
            </button>
          </div>
        )}

        {user && (
          <button
            data-testid="header-points-badge"
            onClick={() => navigate("/rewards")}
            className={`relative bg-[var(--paw-yellow)] text-[#16264F] rounded-full pl-2.5 pr-3.5 py-1.5 flex items-center gap-1.5 shadow-md active:scale-95 transition-transform ${pointsBump ? "points-badge-bump" : ""}`}
          >
            <PawPrint className="w-4 h-4 text-[var(--paw-orange)]" fill="currentColor" />
            <span className="text-sm font-extrabold leading-none">{points}</span>
            <span className="text-[10px] font-bold leading-none opacity-70">pts</span>
            {pointsBump && pointsDelta > 0 && (
              <span className="points-badge-delta" data-testid="header-points-delta">+{pointsDelta}</span>
            )}
          </button>
        )}


        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="bg-white rounded-full pl-2 pr-3 py-1.5 flex items-center gap-2 shadow-md active:scale-95 transition-transform">
              <span className="text-lg leading-none">🇦🇺</span>
              <span className="text-left leading-tight">
                <span className="block text-sm font-extrabold text-slate-900">{suburb}</span>
                <span className="block text-[10px] font-bold text-slate-400 -mt-0.5">VIC, AU</span>
              </span>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 max-h-72 overflow-y-auto">
            <DropdownMenuLabel>Change location</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setSuburb("Melbourne")}>
              {suburb === "Melbourne" && <Check className="w-4 h-4 mr-2" />}
              Melbourne
            </DropdownMenuItem>
            {SUBURBS.map((s) => (
              <DropdownMenuItem key={s} onClick={() => setSuburb(s)}>
                {suburb === s && <Check className="w-4 h-4 mr-2" />}
                {s}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Page content */}
      <main className="relative z-10 px-4">{children}</main>

      {/* Bottom navigation */}
      <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[460px] bg-white border-t border-slate-100 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] z-[1000]">
        <div className="flex items-center justify-around py-2">
          {NAV.map((item) => {
            const active = location.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className="flex flex-col items-center gap-0.5 px-2 py-1 min-w-[56px]"
              >
                <Icon
                  className={`w-6 h-6 transition-colors ${
                    active ? "text-[var(--paw-orange)]" : "text-slate-400"
                  }`}
                  fill={active && (item.label === "Rescues" || item.label === "Lost") ? "currentColor" : "none"}
                />
                <span
                  className={`text-[11px] font-bold ${
                    active ? "text-[var(--paw-orange)]" : "text-slate-400"
                  }`}
                >
                  {item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>

    {/* Menu bottom-sheet drawer */}
    {menuOpen && (
      <div
        data-testid="menu-drawer-backdrop"
        className="fixed inset-0 z-[2000] flex items-end bg-black/45"
        onClick={() => setMenuOpen(false)}
      >
        <div
          data-testid="menu-drawer-sheet"
          className="w-full max-w-[520px] mx-auto max-h-[92vh] bg-[#C7E4F2] rounded-t-[28px] overflow-hidden flex flex-col animate-[slideUp_.25s_ease-out]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center px-6 pt-6 pb-4 border-b border-[#B8D4E5]">
            <div className="flex-1">
              <h2 className="text-[26px] font-black text-[#2D3A52] tracking-tight leading-none">Paww Prints</h2>
              <p className="text-xs font-bold text-[#E07A5F] mt-1">Every paw deserves a home</p>
            </div>
            <button
              data-testid="menu-drawer-close"
              onClick={() => setMenuOpen(false)}
              className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm active:scale-95 transition-transform"
            >
              <X size={22} color="#1B2A4E" />
            </button>
          </div>

          <div className="overflow-y-auto px-6 pt-4 pb-10">
            {user ? (
              <div data-testid="menu-profile" className="flex items-center gap-3 bg-white rounded-2xl p-3 mt-1 mb-4">
                <div className="w-11 h-11 rounded-full bg-[#16264F] flex items-center justify-center text-white font-black text-lg overflow-hidden">
                  {user.picture ? <img loading="lazy" decoding="async" src={user.picture} alt="" className="w-full h-full object-cover" /> : (user.name || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold text-[#2D3A52] truncate">{user.name}</div>
                  <div className="text-xs font-bold text-[#E07A5F]">🏆 {user.points_balance} paw points</div>
                </div>
                <button data-testid="menu-logout" onClick={() => { logout(); setMenuOpen(false); }} className="text-xs font-extrabold text-slate-500 bg-slate-100 rounded-full px-3 py-2 active:scale-95 transition-transform">Log out</button>
              </div>
            ) : (
              <button
                data-testid="menu-signin"
                onClick={() => handleMenuClick({ to: "/auth" })}
                className="w-full flex items-center justify-center gap-2 bg-[#16264F] text-white font-extrabold text-base py-3.5 rounded-[14px] mt-1 mb-4 active:opacity-90 transition-opacity"
              >
                <PawPrint size={18} fill="#fff" color="#fff" />
                Sign in / Sign up
              </button>
            )}

            <div className="flex flex-col gap-1">
              {[...MENU_ITEMS, { label: "📋 My Submissions", sub: "Manage everything you've added", to: "/my" }, ...(user?.is_admin ? [{ label: "🛡️ Admin & Moderation", sub: "Owner-only: approve or remove submissions", to: "/admin" }] : [])].map((m) => (
                <button
                  key={m.label}
                  data-testid={`menu-item-${m.label}`}
                  onClick={() => handleMenuClick(m)}
                  className="text-left flex flex-col gap-0.5 py-3 px-4 rounded-2xl bg-white/70 hover:bg-white active:scale-[0.99] transition"
                >
                  <span className="font-bold text-[#2D3A52]">{m.label}</span>
                  <span className="text-xs text-slate-500">{m.sub}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
}